import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { portalApi } from '../../api/portal';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { Building2, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

export default function PortalJoinByCode() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { refreshCompanies } = useCompany();

  const { data, isLoading, error } = useQuery({
    queryKey: ['join-code-info', code],
    queryFn: () => portalApi.getJoinCodeInfo(code!),
    enabled: !!code,
  });

  const joinMutation = useMutation({
    mutationFn: () => portalApi.joinCompany(code!),
    onSuccess: async (result) => {
      if (result.alreadyMember) {
        toast('You are already a member');
      } else {
        toast.success(`Joined ${result.companyName}!`);
      }
      await refreshCompanies();
      navigate('/portal');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to join'),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 pb-10">
        <div className="flex items-center justify-center py-16">
          <div className="animate-pulse text-muted">Validating code...</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col gap-4 pb-10">
        <div className="card max-w-md mx-auto text-center space-y-4">
          <p className="text-lg font-medium text-fg">Invalid Join Code</p>
          <p className="text-sm text-muted">
            This code may have expired or been deactivated.
          </p>
          <Link to="/portal/join" className="btn btn-primary inline-block">
            Try a different code
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-10">
      <div className="card max-w-md mx-auto text-center space-y-6">
        <div className="w-16 h-16 rounded-2xl brand-gradient flex items-center justify-center mx-auto">
          <Building2 className="w-8 h-8 text-white" />
        </div>

        <div>
          <h1 className="text-xl font-bold text-fg">
            Join {data.company.name}
          </h1>
          {data.company.industry && (
            <p className="text-sm text-muted mt-1">{data.company.industry}</p>
          )}
          {data.organization && (
            <p className="text-sm text-primary mt-2">
              Organization: {data.organization.name}
            </p>
          )}
        </div>

        {data.alreadyMember ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2 text-ok">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">You're already a member</span>
            </div>
            <Link to="/portal" className="btn btn-primary">
              Go to Portal
            </Link>
          </div>
        ) : !isAuthenticated ? (
          <div className="space-y-3">
            <p className="text-sm text-muted">
              You need an account to join. Register or log in first.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to={`/portal/register?code=${code}`} className="btn btn-primary">
                Register
              </Link>
              <Link to={`/login?redirect=/portal/join/${code}`} className="btn btn-secondary">
                Log In
              </Link>
            </div>
          </div>
        ) : (
          <button
            onClick={() => joinMutation.mutate()}
            disabled={joinMutation.isPending}
            className="btn btn-primary disabled:opacity-50"
          >
            {joinMutation.isPending ? 'Joining...' : 'Join Company'}
          </button>
        )}
      </div>
    </div>
  );
}
